'use client'

import Cards from '@/ui/assets/components/cards.inline.svg'
import { GradientBadge } from '@/ui/atoms/badge/Badge'
import { Button } from '@/ui/atoms/button/Button'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { ArrowRight, CreditCard, Globe, Loader2, Shield, Sparkles, Zap } from 'lucide-react'
import Image from 'next/image'

interface CardHeroProps {
  onGetStarted: () => void
  isReturningUser?: boolean
  walletAddress?: string
  isSigningIn?: boolean
}

export function CardHero({ onGetStarted, isReturningUser, walletAddress, isSigningIn }: CardHeroProps) {
  const truncatedAddress = walletAddress
    ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
    : undefined

  const getButtonLabel = () => {
    if (isSigningIn) return 'Signing In...'
    if (isReturningUser) return `Sign In · ${truncatedAddress}`
    return 'Check Eligibility'
  }

  return (
    <section className="relative overflow-hidden py-12 lg:py-20">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="-top-1/2 -translate-x-1/2 absolute left-1/2 h-[600px] w-[600px] animate-pulse rounded-full bg-gradient-to-r from-gradient-yellow/20 to-gradient-green/20 blur-[120px]" />
        <div className="absolute top-1/3 right-0 h-[400px] w-[400px] animate-pulse rounded-full bg-gradient-to-l from-primary/10 to-gradient-green/10 blur-[100px] delay-700" />
      </div>

      <div className="relative z-10 mx-auto max-w-6xl px-4 lg:px-8 sm:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div className="flex flex-col gap-6">
            <GradientBadge className="w-fit" innerClassName="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-gradient-yellow" />
              <Typography variant="prompt" gradient>
                Now Available
              </Typography>
            </GradientBadge>

            <div className="space-y-3">
              <Typography variant="h1" className="text-3xl lg:text-5xl" gradient>
                Hold Everything. Spend Anywhere.
              </Typography>
              <Typography variant="p" className="max-w-lg font-medium text-base text-muted-foreground lg:text-lg">
                No off-ramps. No top-ups. Just tap and spend.
              </Typography>
            </div>

            <Typography variant="p" className="max-w-lg text-muted-foreground">
              The HypurrFi Card borrows against your crypto at the moment of purchase. Your assets stay on-chain while
              you shop at over 150 million merchants, available in select jurisdictions.
            </Typography>

            <div className="flex flex-wrap gap-4 pt-2">
              <Button
                variant="primary"
                size="lg"
                disabled={isSigningIn}
                prefixIcon={
                  isSigningIn ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isReturningUser ? (
                    <Image src="/tokens/hype.png" alt="HyperEVM" width={20} height={20} />
                  ) : undefined
                }
                postfixIcon={!isReturningUser && !isSigningIn ? <ArrowRight className="h-4 w-4" /> : undefined}
                onClick={onGetStarted}
                className="bg-gradient-to-r from-gradient-yellow to-gradient-green text-black hover:opacity-90"
              >
                {getButtonLabel()}
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={() => {
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
                }}
              >
                Learn More
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-6">
              <TrustBadge icon={<Globe className="h-3.5 w-3.5" />} text="150M+ merchants" />
              <TrustBadge icon={<Shield className="h-3.5 w-3.5" />} text="No credit check" />
              <TrustBadge icon={<Zap className="h-3.5 w-3.5" />} text="Real-time credit" />
              <TrustBadge icon={<CreditCard className="h-3.5 w-3.5" />} text="Anywhere Visa is accepted" />
            </div>
          </div>

          <div className="relative flex items-center justify-center lg:justify-end">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-[300px] w-[400px] rounded-full bg-gradient-to-br from-gradient-yellow/30 to-gradient-green/30 blur-[80px]" />
            </div>
            <div
              className={cn(
                'relative transition-all duration-500 hover:scale-105',
                'drop-shadow-[0_0_60px_rgba(251,229,114,0.3)]',
              )}
            >
              <Cards className="h-auto w-full max-w-xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function TrustBadge({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-border/50 bg-panel/50 px-3 py-1.5 backdrop-blur-sm">
      <span className="text-dim-foreground">{icon}</span>
      <span className="text-muted-foreground text-sm">{text}</span>
    </div>
  )
}
